import { Check } from "lucide-react";
import { Button, EmptyState } from "@a2z/ui";
import { useI18n, useTranslate } from "@/lib/i18n";
import { billPlaceLabel } from "@/lib/invoice/place";
import type {
  KitchenBoardPastPlate,
  KitchenBoardPlate,
  KitchenStation,
  KitchenWorkflow,
} from "@/lib/api/types";
import { identityLine, isOffPremise, totalAgeMinutes } from "../lib/channel";
import { buildOrderCards, type OrderCard, type OrderStationState } from "../lib/orderView";
import { retainedMarkOf } from "../lib/ageOff";
import { listSeparator } from "../lib/batch";
import { laneOfState } from "../lib/workflow";
import { ChannelShape, useMinutesLabel, type KitchenCommand } from "./PaperKitchen";

/**
 * «حسب الطلب» — THE ORDER VIEW (T13, T5; D170).
 *
 * The expediter's board: one card per bill — its `#`, place, the off-premise
 * identity and the guest's total wait — then one row per station with that
 * station's state in the board's own lane words, «مكتمل» ✓ only when nothing
 * is queued or cooking (else «بانتظار: {stations}»), and ONE hand-over:
 * «تقديم الطلب (n)» on dine-in, «تسليم الطلب (n)» off-premise. The hand-over
 * serves `handOverStates(workflow)` as one `markServed` for the bill (plan
 * §3.1): READY where the screen records ready, every started plate in paper
 * + pass, where nobody taps «جاهز» and «مكتمل» would otherwise never show.
 * It reads every station within the session's D128 scope.
 *
 * The retained cards (T7; gate P4 #2/#4) follow the live orders, read-only:
 * one per closed bill, rows by the plates' own stamps, the bill's word
 * («مدفوع» settled, «أُغلقت الفاتورة» closed) where «مكتمل» would be, and
 * «إخفاء» instead of a hand-over. Age-off is the page's (whole orders).
 */
export function KitchenOrderView({
  workflow,
  plates,
  stations,
  now,
  busyItemIds,
  canServe,
  newKeys,
  onRun,
  paid = [],
  onHidePaid,
}: {
  workflow: KitchenWorkflow;
  plates: readonly KitchenBoardPlate[];
  /** Retained plates of closed bills (T7), already age-filtered by the page. */
  paid?: readonly KitchenBoardPastPlate[];
  onHidePaid?: (plateIds: string[]) => void;
  stations: readonly KitchenStation[];
  now: number;
  busyItemIds: ReadonlySet<string>;
  canServe: boolean;
  newKeys?: ReadonlySet<string>;
  onRun: (plates: KitchenBoardPlate[], type: KitchenCommand) => Promise<boolean | void>;
}) {
  const t = useTranslate();
  const { locale } = useI18n();
  const minutesLabel = useMinutesLabel();
  const cards = buildOrderCards(plates, workflow, stations);
  /* A retained plate is SERVED on the wire (the close collapsed it); its row
     is drawn by its stamps, as the lanes place it — ready, else cooking. */
  const paidById = new Map(paid.map((plate) => [plate.id, plate]));
  const paidCards = buildOrderCards(
    paid.map((plate) => ({ ...plate, fulfillmentState: plate.readyAt ? ("READY" as const) : ("PREPARING" as const) })),
    workflow,
    stations,
  );
  const stationName = (id: string | null) => {
    const station = id === null ? undefined : stations.find((one) => one.id === id);
    if (!station) return t((m) => m.kitchen.availabilityUnassigned);
    return locale === "en" && station.nameEn ? station.nameEn : station.nameAr;
  };
  const stateWord = (state: OrderStationState) => {
    const lane = laneOfState(workflow, state) ?? state;
    return t((m) =>
      lane === "QUEUED" ? m.kitchen.laneQueued : lane === "PREPARING" ? m.kitchen.lanePreparing : m.kitchen.laneReady,
    );
  };
  const dishName = (plate: KitchenBoardPlate) =>
    locale === "en" && plate.nameEn ? plate.nameEn : plate.nameAr;

  if (cards.length === 0 && paidCards.length === 0) {
    return (
      <EmptyState
        title={t((m) => m.kitchen.orderEmptyTitle)}
        description={t((m) => m.kitchen.orderEmptyBody)}
      />
    );
  }

  return (
    <div data-ui="kitchen-orders" data-testid="kitchen-orders" className="grid items-start gap-3">
      {[...cards.map((card) => ({ card, retained: false })), ...paidCards.map((card) => ({ card, retained: true }))].map(({ card, retained }: { card: OrderCard; retained: boolean }) => {
        const offPremise = isOffPremise(card.first.channel);
        const identity = offPremise ? identityLine(card.first) : null;
        const age = totalAgeMinutes(card.plates, now);
        const busy = card.plates.some((plate) => busyItemIds.has(plate.id));
        const count = card.handOver.length;
        const mark = retained ? retainedMarkOf(paidById.get(card.first.id) ?? {}) : null;
        const key = retained ? `paid:${card.invoiceId}` : card.invoiceId;
        return (
          <article
            key={key}
            data-ui="kitchen-order-card"
            data-testid={`kitchen-order-${key}`}
            data-paid={retained ? "" : undefined}
            data-complete={card.complete && !retained ? "" : undefined}
            data-new={newKeys?.has(card.invoiceId) ? "" : undefined}
            data-channel={card.first.channel}
            className="flex flex-col"
          >
            <header data-ui="kitchen-order-head" className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span data-ui="kitchen-ticket-number" className="whitespace-nowrap tabular-nums">
                #{card.first.dailySequence}
              </span>
              <span data-ui="kitchen-ticket-place" className="flex items-center gap-1 truncate">
                <ChannelShape channel={card.first.channel} />
                <span className="truncate">
                  {billPlaceLabel({ tableIds: card.first.tableNames, channel: card.first.channel }, (name) => name, t)}
                </span>
              </span>
              <span className="flex-1" />
              {age !== null ? (
                <span data-ui="kitchen-ticket-age" className="whitespace-nowrap tabular-nums">
                  {minutesLabel.totalAge(age)}
                </span>
              ) : null}
            </header>
            {identity ? (
              <div data-ui="kitchen-ticket-identity" className="flex">
                <bdi data-ui="kitchen-ticket-who">{identity}</bdi>
              </div>
            ) : null}
            <ul data-ui="kitchen-order-stations" className="flex flex-col">
              {card.stations.map((row) => {
                const dishes = new Map<string, number>();
                for (const plate of row.plates) dishes.set(dishName(plate), (dishes.get(dishName(plate)) ?? 0) + 1);
                return (
                  <li
                    key={row.stationId ?? "none"}
                    data-ui="kitchen-order-station"
                    // The LANE the state is drawn in (paper + pass and one-tap draw
                    // QUEUED as «في التحضير»), so the word and its tone agree.
                    data-state={laneOfState(workflow, row.state) ?? row.state}
                    data-plate-state={row.state}
                    data-testid={`kitchen-order-station-${card.invoiceId}-${row.stationId ?? "none"}`}
                    className="flex flex-col"
                  >
                    <span className="flex flex-wrap items-center gap-x-2">
                      <span data-ui="kitchen-order-station-name">{stationName(row.stationId)}</span>
                      <span className="flex-1" />
                      {row.unprinted ? (
                        <span data-ui="kitchen-ticket-unprinted">{t((m) => m.kitchen.unprinted)}</span>
                      ) : null}
                      <span data-ui="kitchen-order-station-state">{stateWord(row.state)}</span>
                    </span>
                    <span data-ui="kitchen-order-dishes">
                      <bdi>
                        {[...dishes].map(([name, n]) => `${n}× ${name}`).join("  ·  ")}
                      </bdi>
                    </span>
                  </li>
                );
              })}
            </ul>
            <p data-ui="kitchen-order-status" data-testid={`kitchen-order-status-${card.invoiceId}`} className="flex items-center gap-1.5">
              {mark ? (
                <span data-ui="kitchen-ticket-paid" data-mark={mark} data-testid={`kitchen-order-paid-${card.invoiceId}`}>
                  {mark === "paid" ? t((m) => m.kitchen.paidMark) : t((m) => m.kitchen.billClosedMark)}
                </span>
              ) : card.complete ? (
                <>
                  <Check size={16} strokeWidth={2.5} aria-hidden="true" />
                  {t((m) => m.kitchen.orderComplete)}
                </>
              ) : (
                t((m) => m.kitchen.orderWaitingOn, {
                  stations: card.waitingOn.map(stationName).join(listSeparator(locale)),
                })
              )}
            </p>
            {retained ? (
              onHidePaid ? (
                <footer data-ui="kitchen-ticket-foot" className="flex">
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`kitchen-order-hide-${card.invoiceId}`}
                    onClick={() => onHidePaid(card.plates.map((plate) => plate.id))}
                  >
                    {t((m) => m.kitchen.hide)}
                  </Button>
                </footer>
              ) : null
            ) : canServe ? (
              <footer data-ui="kitchen-ticket-foot" className="flex">
                <Button
                  variant="primary"
                  size="sm"
                  data-kind="serve"
                  disabled={busy || count === 0}
                  data-testid={`kitchen-order-handover-${card.invoiceId}`}
                  onClick={() => void onRun(card.handOver, "markServed")}
                >
                  {offPremise
                    ? t((m) => m.kitchen.orderHandOver, { count })
                    : t((m) => m.kitchen.orderServe, { count })}
                </Button>
              </footer>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}
